import { useQuery } from '@tanstack/react-query'
import { useContext, useEffect } from 'react'
import { isAxiosError } from 'axios'
import dayjs from 'dayjs'
import styled from 'styled-components'

import { QUERY_KEY } from '../../constants'

import { toast } from '../../utils'

import { StoresContext } from '../../provider/StoreProvider'

import { useNavigate } from '../../hooks/useNavigate'
import useParamId from '../../hooks/useParamId'
import { useUserInfo } from '../../hooks/useUserInfo'

import { getUserProductDetail } from '../../api/product'

import { Wrap } from '../../components/container/Wrap'
import { Content } from '../../components/container/Content'
import { AlignCenterItem, CenterWrap, ColumnItem } from '../../components/common'
import { Card } from '../../components/card/Card'
import { ProductInfoCard } from '../../components/card/ProductInfoCard'
import { Text } from '../../components/text'
import { DivideLine } from '../../components/divide'
import { NoticeContainer } from '../../components/noticeContainer/NoticeContainer'
import { Header } from '../../components/header'
import { Spinner } from '../../components/spinner/Spinner'
import { RowData } from './components'

import { Color } from '../../theme/color'

import { ReactComponent as ArrowRightGrayIcon } from '../../assets/ico/common/arrow/right/arrow_right_gray_18.svg'

export function SaleInfo() {
  const {
    commonStore: { setInvalidAccess },
  } = useContext(StoresContext)

  const navigate = useNavigate()
  const paramId = useParamId(3)
  const { isLogin, isCheckingLogin } = useUserInfo()

  const {
    data: userProduct,
    isPending,
    isError,
    error,
  } = useQuery({
    queryKey: [QUERY_KEY.userProduct, paramId],
    queryFn: async () => await getUserProductDetail(paramId as number),
    enabled: typeof paramId === 'number',
  })

  useEffect(() => {
    if (!isLogin && !isCheckingLogin) setInvalidAccess(true)
  }, [isLogin, isCheckingLogin])

  useEffect(() => {
    if (isError && error) {
      if (isAxiosError(error)) {
        toast(error.response?.data.message, error)
      }
    }
  }, [isError, error])

  if (isPending || isError) {
    return (
      <Wrap>
        <CenterWrap>
          <Spinner />
        </CenterWrap>
      </Wrap>
    )
  }

  const goProductDetail = () => {
    navigate(`/market/product/${userProduct.product.id}`)
  }

  return (
    <Wrap>
      <Header type="SUB" title="매각 정보" />
      <Content>
        <ProductInfoWrap>
          <ProductInfoCard>
            <ProductInfoContent $gap={16}>
              <ProductImage>
                <img src={userProduct.product.imageUri} alt={userProduct.product.name} />
              </ProductImage>
              <ColumnItem $gap={4}>
                <Text size={18} weight={600}>
                  {userProduct.product.artist}
                </Text>
                <Text size={14} color="text_secondary">
                  {userProduct.product.name}
                </Text>
              </ColumnItem>
              <ProductDetailLink>
                <Text size={14} weight={500} color="text_secondary" onClick={goProductDetail}>
                  작품 상세 보기
                </Text>
                <ArrowRightGrayIcon />
              </ProductDetailLink>
            </ProductInfoContent>
            <DivideLine />
            <ColumnItem $gap={8}>
              <RowData title="총 배정 긍권 수" content={`${userProduct.totalShares.toLocaleString()}주`} />
              <Card $direction="column" $gap={8} $padding="16px" $radius={8} $background="gray_1">
                <RowData
                  title={{ value: '• 사업자 배정 수', size: 14 }}
                  content={{
                    value: `${userProduct.product.activeProductSale?.preAllottedShares.toLocaleString()}주`,
                    size: 14,
                  }}
                />
                <RowData
                  title={{ value: '• 일반 배정 수', size: 14 }}
                  content={{
                    value: `${(
                      (userProduct.product.activeProductSale?.totalIssuedShares ?? 0) -
                      (userProduct.product.activeProductSale?.preAllottedShares ?? 0)
                    ).toLocaleString()}주`,
                    size: 14,
                  }}
                />
              </Card>
            </ColumnItem>
            <RowData
              title="총 발행 금액"
              content={`${userProduct.product.activeProductSale?.totalIssuedPrice.toLocaleString()}원`}
            />
            <RowData
              title="주당 공모가 액"
              content={`${userProduct.product.activeProductSale?.offerPrice.toLocaleString()}원`}
            />
            <RowData
              title="배정 확정일"
              content={`${dayjs(userProduct.product.activeProductSale?.allotmentDate).format('YYYY.MM.DD')}`}
            />
            <RowData title="예상 매각 금액" content={`${userProduct.balance.toLocaleString()}원`} />
            <DivideLine />
            <Text size={18} weight={600}>
              예상 수익
            </Text>
            <RowData title="배정 금액" content={`${userProduct.balance.toLocaleString()}원`} />
            <RowData title="배정 수" content={`${userProduct.balance.toLocaleString()}주`} />
            <RowData title={{ value: '예상 수익률', color: 'error' }} content={{ value: `+20%`, color: 'error' }} />
            <RowData
              title={{ value: '예상 수익금', color: 'error' }}
              content={{
                value: `${userProduct.balance.toLocaleString()}원`,
                weight: 500,
                color: 'error',
              }}
            />
          </ProductInfoCard>
        </ProductInfoWrap>
        <NoticeContainer
          title="안내사항"
          list={[
            '작품 매각이 완료되면 정산 절차가 진행될 예정이에요. 추후 매각 대금 정산을 위한 절차 진행 부탁드려요.',
            '투자 계약서 내에 명시된 양도 소득세 및 제세 공과금, 성과보수 등의 비용을 차 감시 실제 수익률은 달라질 수 있어요.',
          ]}
          $backgroundColor="gray_1"
        />
      </Content>
    </Wrap>
  )
}

const ProductInfoWrap = styled.div`
  padding: 16px;
  background-color: ${Color.gray_1};
`

const ProductInfoContent = styled(AlignCenterItem)`
  position: relative;
`

const ProductImage = styled.div`
  overflow: hidden;
  width: 100px;
  aspect-ratio: 1/1.3;
  border-radius: 8px;
  > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const ProductDetailLink = styled(AlignCenterItem)`
  position: absolute;
  right: 0;
  bottom: 0;
  border: none;
  outline: none;
`
